import type {
  BagAllowance,
  BagType,
  BagTypeAvailability,
  BagTypeGroup,
  BookableBag,
  EligibleLeg,
  Leg,
  Passenger,
  PassengerBag,
  PassengerBagByPassengerType,
  PassengerBagLeg,
  PaxType,
  AllPassengerBag,
  SelectedBag,
  SelectedBags,
  TripType,
} from '@optionway/models';
import { BagTypeEnum, modelsImpl, PaxTypeEnum } from '@optionway/models';

interface LuggagesStore {
  selectedBagsForAll: SelectedBags;
}

export const useLuggagesStore = defineStore('luggages', {
  state: (): LuggagesStore => {
    const initBagsForAll = new modelsImpl.SelectedBagsImpl();
    return { selectedBagsForAll: toPojo(initBagsForAll) };
  },
  getters: {
    bagTypeGroup(): BagTypeGroup {
      const offer = useSelectedOfferStore().offer;
      return this.bagTypeGroupOf(offer?.bookableBags ?? [], offer?.includedBags ?? []);
    },
    bagTypeGroupOf(): (allBookableBags: BookableBag[], allIncludedBags: BagAllowance[]) => BagTypeGroup {
      const groupedPassengers = usePassengersStore().groupedByType;

      return (allBookableBags: BookableBag[], allIncludedBags: BagAllowance[]) => ({
        HAND: buildBagTypeGroupEntry('HAND', groupedPassengers, allBookableBags, allIncludedBags),
        CABIN: buildBagTypeGroupEntry('CABIN', groupedPassengers, allBookableBags, allIncludedBags),
        CHECKED: buildBagTypeGroupEntry('CHECKED', groupedPassengers, allBookableBags, allIncludedBags),
      });
    },
    bestPriceBookableBags(): (bagType: BagType) => BookableBag | undefined {
      const selectedOffer = useSelectedOfferStore();

      return (bagType: BagType) => selectedOffer.offer?.bestPriceBookableBags?.[bagType];
    },
    tripType(): TripType | undefined {
      return useSelectedOfferStore().offer?.flight.tripType;
    },
    allLegs(): Leg[] {
      return useSelectedOfferStore().offer?.flight.legs ?? [];
    },
    allPax(): Passenger[] {
      return usePassengersStore().passengers;
    },
    presentPaxTypes(): PaxType[] {
      return [...new Set(this.allPax.map((pax) => pax.type))];
    },
    bagTypeList: (): BagType[] => [BagTypeEnum.HAND, BagTypeEnum.CABIN, BagTypeEnum.CHECKED],
    // Compares the resolved allowance of this pax type against every other present pax
    // type, channel-agnostically — reading byFlight only would miss LEG-scoped allowances, which is
    // the default channel for any allowance that differs between legs.
    // Known limitation: only the resolved allowance is compared, not the full per-pax-type list, so
    // a pax type owning a second included allowance the others lack is not reported.
    includedBagDiffersForPaxType(): (bagType: BagType, paxType: PaxType) => boolean {
      const group = this.bagTypeGroup;
      const presentPaxTypes = this.presentPaxTypes;
      const includedBagForPaxType = this.includedBagForPaxType;
      return (bagType: BagType, paxType: PaxType) => {
        // no passenger of this type → allowance cannot differ for them
        if (!presentPaxTypes.includes(paxType)) return false;
        // ALL_PAX channels give everyone the same allowance → nobody differs. Kept as an explicit
        // guard: includedBagForPaxType scans a flat list, so a payload mixing ALL_PAX and per-pax
        // channels would otherwise resolve a different allowance per pax type on list order alone.
        if (
          group[bagType].includedBags.some((b) => b.bookableBy === 'ALL_PAX_FLIGHT' || b.bookableBy === 'ALL_PAX_LEG')
        )
          return false;
        const target = includedBagForPaxType(bagType, paxType);
        return presentPaxTypes
          .filter((type) => type !== paxType)
          .some((type) => {
            const other = includedBagForPaxType(bagType, type);
            // one has an allowance and the other none → differs; both present → compare specs
            if (!target || !other) return target !== other;
            return !bagsHaveSameSpecAndPieces(target, other);
          });
      };
    },
    includedOnlyForPaxType(): (bagType: BagType, paxType: PaxType) => boolean {
      const group = this.bagTypeGroup;
      const presentPaxTypes = this.presentPaxTypes;
      const includedBagForPaxType = this.includedBagForPaxType;
      return (bagType: BagType, paxType: PaxType) => {
        // no passenger of this type → nothing to compare
        if (group[bagType].includedBags.length === 0 || !presentPaxTypes.includes(paxType)) return false;
        return (
          !!includedBagForPaxType(bagType, paxType) &&
          presentPaxTypes.every((type) => type === paxType || !includedBagForPaxType(bagType, type))
        );
      };
    },
    includedExceptForPaxType(): (bagType: BagType, paxType: PaxType) => boolean {
      const group = this.bagTypeGroup;
      const presentPaxTypes = this.presentPaxTypes;
      const includedBagForPaxType = this.includedBagForPaxType;
      return (bagType: BagType, paxType: PaxType) => {
        if (group[bagType].includedBags.length === 0 || !presentPaxTypes.includes(paxType)) return false;
        return (
          !includedBagForPaxType(bagType, paxType) &&
          presentPaxTypes.every((type) => type === paxType || !!includedBagForPaxType(bagType, type))
        );
      };
    },
    includedBagForPaxType(): (bagType: BagType, paxType: PaxType) => BagAllowance | undefined {
      const group = this.bagTypeGroup;
      const presentPaxTypes = this.presentPaxTypes;
      return (bagType: BagType, paxType: PaxType) => bagForPax(group[bagType].includedBags, paxType, presentPaxTypes);
    },
    includedBagForPaxTypeOnLeg(): (bagType: BagType, paxType: PaxType, legId: number) => BagAllowance | undefined {
      const group = this.bagTypeGroup;
      const presentPaxTypes = this.presentPaxTypes;
      return (bagType: BagType, paxType: PaxType, legId: number) =>
        bagForPax(
          group[bagType].includedBags.filter((bag) => isEligibleOnLeg(bag, legId)),
          paxType,
          presentPaxTypes,
        );
    },
    selectedCount(): (bagType: BagType) => number {
      const totals: Partial<Record<BagType, number>> = {};
      for (const bagType of this.bagTypeList) {
        totals[bagType] =
          selectedPiecesForPax(bagType, this.selectedBagsForAll) +
          getAllPax(this.bagTypeGroup[bagType]).reduce(
            (sum, pax) => sum + selectedPiecesForPax(bagType, pax.selectedBags),
            0,
          );
      }
      return (bagType: BagType) => totals[bagType] ?? 0;
    },
    selectedBagTypes(): BagType[] {
      return this.bagTypeList.filter((bagType) => this.selectedCount(bagType) > 0);
    },
    hasSelectedBags(): boolean {
      return this.selectedBagTypes.length > 0;
    },
    selectedBagsTotalPrice(): number {
      return this.selectedBagsTotalPriceIn(this.bagTypeGroup);
    },
    selectedBagsTotalPriceFor(): (bookableBags: BookableBag[]) => number {
      const bagTypeGroupOf = this.bagTypeGroupOf;
      const totalPriceIn = this.selectedBagsTotalPriceIn;
      // included bags carry no price: only the bookable ones are dispatched
      return (bookableBags: BookableBag[]) => totalPriceIn(bagTypeGroupOf(bookableBags, []));
    },
    selectedBagsTotalPriceIn(): (group: BagTypeGroup) => number {
      const priceByTypeIn = this.selectedBagsPriceByTypeIn;
      const bagTypeList = this.bagTypeList;
      return (group: BagTypeGroup) => bagTypeList.reduce((sum, bagType) => sum + priceByTypeIn(group, bagType), 0);
    },
    selectedBagsPriceByType(): (bagType: BagType) => number {
      const priceByTypeIn = this.selectedBagsPriceByTypeIn;
      const group = this.bagTypeGroup;
      return (bagType: BagType) => priceByTypeIn(group, bagType);
    },
    selectedBagsPriceByTypeIn(): (group: BagTypeGroup, bagType: BagType) => number {
      const selectedBagsForAll = this.selectedBagsForAll;
      return (group: BagTypeGroup, bagType: BagType) => {
        const { bookableBags } = group[bagType];
        return (
          sumSelectedBagsPrice(bookableBags, bagType, selectedBagsForAll) +
          getAllPax(group[bagType]).reduce(
            (sum, pax) => sum + sumSelectedBagsPrice(bookableBags, bagType, pax.selectedBags),
            0,
          )
        );
      };
    },
    totalBagCount(): (bagType: BagType) => number {
      const allPax = this.allPax;
      const forPax = this.bagCountForPax;
      // Per pax, not summed then maxed: each passenger's own max folds a bag carried on several legs into one, and two
      // passengers served on one leg each would otherwise cancel out.
      return (bagType: BagType) => allPax.reduce((sum, pax) => sum + forPax(bagType, pax), 0);
    },
    bagCountPerLegForPax(): (bagType: BagType, pax: Passenger) => number[] {
      const group = this.bagTypeGroup;
      const allLegs = this.allLegs;
      const selectedForAll = this.selectedBagsForAll;
      const bagTypeList = this.bagTypeList;
      // Reading selectedBags and getPaxBag here rather than in the returned closure is what makes this
      // getter invalidate on a selection change; the closure is then a plain lookup.
      const countsByPax = new Map<Passenger, Partial<Record<BagType, number[]>>>();
      for (const pax of this.allPax) {
        const byType: Partial<Record<BagType, number[]>> = {};
        for (const bagType of bagTypeList) {
          const entry = group[bagType];
          byType[bagType] = totalCountsPerLeg(
            [entry.allPassenger, getPaxBag(entry, pax)],
            [pax.selectedBags, selectedForAll],
            bagType,
            allLegs,
          );
        }
        countsByPax.set(pax, byType);
      }
      return (bagType: BagType, pax: Passenger) => countsByPax.get(pax)?.[bagType] ?? [];
    },
    bagCountForPax(): (bagType: BagType, pax: Passenger) => number {
      const perLeg = this.bagCountPerLegForPax;
      // physical piece count for the trip = max across legs (a bag carried on several legs counts once)
      return (bagType: BagType, pax: Passenger) => Math.max(0, ...perLeg(bagType, pax));
    },
    bagCountMergedLegsForPax(): (bagType: BagType, pax: Passenger) => number | undefined {
      const perLeg = this.bagCountPerLegForPax;
      // A one-way trip (single leg) is trivially merged.
      return (bagType: BagType, pax: Passenger) => mergedIfUniform(perLeg(bagType, pax));
    },
    // Undefined when the legs carrying the bag do not share one count, or when none carries it.
    bagCountMergedCarryingLegsForPax(): (bagType: BagType, pax: Passenger) => number | undefined {
      const perLeg = this.bagCountPerLegForPax;
      return (bagType: BagType, pax: Passenger) => {
        const carryingCounts = perLeg(bagType, pax).filter((count) => count > 0);
        return carryingCounts.length > 0 ? mergedIfUniform(carryingCounts) : undefined;
      };
    },
    legsWithoutBagForPax(): (bagType: BagType, pax: Passenger) => Leg[] {
      const allLegs = this.allLegs;
      const perLeg = this.bagCountPerLegForPax;
      return (bagType: BagType, pax: Passenger) => {
        const counts = perLeg(bagType, pax);
        return allLegs.filter((_, index) => !counts[index]);
      };
    },
    // All-pax per-leg count: every passenger's per-leg counts summed leg by leg (indexed like allLegs).
    bagCountPerLegForAll(): (bagType: BagType) => number[] {
      const allPax = this.allPax;
      const perLegForPax = this.bagCountPerLegForPax;
      return (bagType: BagType) => {
        const totals: number[] = [];
        for (const pax of allPax) {
          const perLeg = perLegForPax(bagType, pax);
          for (const [legId, count] of perLeg.entries()) {
            totals[legId] = (totals[legId] ?? 0) + count;
          }
        }
        return totals;
      };
    },
    bagCountMergedLegsForAll(): (bagType: BagType) => number | undefined {
      const perLeg = this.bagCountPerLegForAll;
      return (bagType: BagType) => mergedIfUniform(perLeg(bagType));
    },
    // Excludes selectedSources, so the airline's allowance stays independent of what was purchased.
    includedBagCountPerLegForPax(): (bagType: BagType, pax: Passenger) => number[] {
      const group = this.bagTypeGroup;
      const legs = this.allLegs;
      const bagTypeList = this.bagTypeList;
      const countsByPax = new Map<Passenger, Partial<Record<BagType, number[]>>>();
      for (const pax of usePassengersStore().passengers) {
        const byType: Partial<Record<BagType, number[]>> = {};
        for (const bagType of bagTypeList) {
          const entry = group[bagType];
          const includedSources = [entry.allPassenger, getPaxBag(entry, pax)];
          byType[bagType] = includedCountsPerLeg(includedSources, legs);
        }
        countsByPax.set(pax, byType);
      }
      return (bagType: BagType, pax: Passenger) => countsByPax.get(pax)?.[bagType] ?? [];
    },
    includedBagCountPerLegForAll(): (bagType: BagType) => number[] {
      const allPax = this.allPax;
      const perLegForPax = this.includedBagCountPerLegForPax;
      return (bagType: BagType) => {
        const totals: number[] = [];
        for (const pax of allPax) {
          const perLeg = perLegForPax(bagType, pax);
          for (const [legId, count] of perLeg.entries()) {
            totals[legId] = (totals[legId] ?? 0) + count;
          }
        }
        return totals;
      };
    },
    includedBagCountMergedLegsForAll(): (bagType: BagType) => number | undefined {
      const perLeg = this.includedBagCountPerLegForAll;
      return (bagType: BagType) => mergedIfUniform(perLeg(bagType));
    },
    includedBagCountForLeg(): (bagType: BagType, legId: number) => number {
      const perLeg = this.includedBagCountPerLegForAll;
      return (bagType: BagType, legId: number) => perLeg(bagType)[legId] ?? 0;
    },
    totalBagCountForLeg(): (bagType: BagType, legId: number) => number {
      const perLeg = this.bagCountPerLegForAll;
      return (bagType: BagType, legId: number) => perLeg(bagType)[legId] ?? 0;
    },
    // Compares includedBagForPaxTypeOnLeg rather than compatibleIncludedBagsMergedLegsForAll: the
    // latter is gated by cross-pax-type compatibility, so a bag type ineligible for one pax type
    // (infants without a HAND allowance) would always read as varying even when every leg is identical.
    includedBagSpecVariesAcrossLegsForPaxType(): (bagType: BagType, paxType: PaxType) => boolean {
      const hasIncludedBags = this.hasIncludedBags;
      const allLegs = this.allLegs;
      const includedBagForPaxTypeOnLeg = this.includedBagForPaxTypeOnLeg;
      return (bagType: BagType, paxType: PaxType) => {
        const [firstLeg, ...otherLegs] = allLegs;
        if (!hasIncludedBags(bagType) || !firstLeg || allLegs.length < 2) return false;
        const reference = includedBagForPaxTypeOnLeg(bagType, paxType, firstLeg.id ?? 0);
        return otherLegs.some((leg) => {
          const candidate = includedBagForPaxTypeOnLeg(bagType, paxType, leg.id ?? 0);
          if (!reference || !candidate) return reference !== candidate;
          return !bagsHaveSameSpecAndPieces(reference, candidate);
        });
      };
    },
    // Per pax, not summed: two passengers each served on one leg only would otherwise cancel out.
    totalBagCountVariesForAnyPax(): (bagType: BagType) => boolean {
      const allPax = this.allPax;
      const perLegForPax = this.bagCountPerLegForPax;
      return (bagType: BagType) =>
        allPax.some((pax) => {
          const counts = perLegForPax(bagType, pax);
          return counts.length > 0 && mergedIfUniform(counts) === undefined;
        });
    },
    eligibleLegsForAll(): (bagType: BagType) => EligibleLeg[] {
      const group = this.bagTypeGroup;
      return (bagType: BagType) => {
        const byLeg = group[bagType].allPassenger?.byLeg;
        return byLeg ? Object.values(byLeg).map((d) => d.eligibleLeg) : [];
      };
    },
    compatibleBagsByFlightForAllPax(): (bagType: BagType) => BookableBag[] {
      const group = this.bagTypeGroup;
      return (bagType: BagType) => {
        const entry = group[bagType];
        return compatibleAcrossPaxGroups(entry, (paxBag) => paxBag?.byFlight?.bookableBags ?? []).map((bag) =>
          withAllPaxCombinedPrice(bag, entry),
        );
      };
    },

    compatibleBagsByLegForAllPax(): (bagType: BagType, legIndex: number) => BookableBag[] {
      const group = this.bagTypeGroup;
      return (bagType: BagType, legIndex: number) => {
        const entry = group[bagType];
        return compatibleAcrossPaxGroups(entry, (paxBag) => paxBag?.byLeg?.[legIndex]?.bookableBags ?? []).map((bag) =>
          withAllPaxCombinedPrice(bag, entry, legIndex),
        );
      };
    },
    // Included bags --
    compatibleIncludedBagsByFlightForAllPax(): (bagType: BagType) => BagAllowance[] {
      const group = this.bagTypeGroup;
      return (bagType: BagType) =>
        compatibleAcrossPaxGroups(group[bagType], (paxBag) => paxBag?.byFlight?.includedBags ?? []);
    },
    compatibleIncludedBagsByLegForAllPax(): (bagType: BagType, legIndex: number) => BagAllowance[] {
      const group = this.bagTypeGroup;
      return (bagType: BagType, legIndex: number) =>
        compatibleAcrossPaxGroups(group[bagType], (paxBag) => paxBag?.byLeg?.[legIndex]?.includedBags ?? []);
    },
    hasBookableBags(): (bagType: BagType) => boolean {
      const group = this.bagTypeGroup;
      return (bagType: BagType) => group[bagType].bookableBags.length > 0;
    },
    hasIncludedBags(): (bagType: BagType) => boolean {
      const group = this.bagTypeGroup;
      return (bagType: BagType) => group[bagType].includedBags.length > 0;
    },
    hasBookableBagsForAnyType(): boolean {
      return this.bagTypeList.some((type) => this.hasBookableBags(type));
    },
    customizeByPassengerAllowed(): (bagType: BagType) => boolean {
      const compatibleBagsMergedLegsForAll = this.compatibleBagsMergedLegsForAll;
      const compatibleBagsByFlightForAllPax = this.compatibleBagsByFlightForAllPax;
      const compatibleBagsByLegForAllPax = this.compatibleBagsByLegForAllPax;
      const group = this.bagTypeGroup;
      return (bagType: BagType) => {
        const entry = group[bagType];
        const legIndices = getAllLegIndices(entry);
        return (
          countTotalPax(entry) >= 2 &&
          (compatibleBagsMergedLegsForAll(bagType).length > 0 || // UC5
            compatibleBagsByFlightForAllPax(bagType).length > 0 || // UC7
            legIndices.some((legIndex) => compatibleBagsByLegForAllPax(bagType, legIndex).length > 0)) // UC8
        );
      };
    },
    compatibleBagsMergedLegsForAll(): (bagType: BagType) => BookableBag[] {
      const group = this.bagTypeGroup;
      const allLegs = this.allLegs;
      const eligibleLegsForCompatiblePaxList = this.eligibleLegsForCompatiblePaxList;
      const compatibleBagsByLegForAllPax = this.compatibleBagsByLegForAllPax;
      return (bagType: BagType) => {
        const entry = group[bagType];
        if (allLegs.length === 0) return [];
        const allPaxLegs = getEligibleLegsForAll(entry);
        if (allPaxLegs.length > 0) {
          const allPaxLegBagLists = allLegs.map((leg) => entry.allPassenger?.byLeg?.[leg.id ?? 0]?.bookableBags ?? []);
          if (allLegs.length === 1) return allPaxLegBagLists[0] ?? [];
          return allLegsBagsAreSymmetric(allPaxLegBagLists)
            ? (allPaxLegBagLists[0] ?? []).map((bag) => withAllLegsCombinedPrice(bag, allPaxLegBagLists))
            : [];
        }
        const paxLegs = eligibleLegsForCompatiblePaxList(bagType);
        if (paxLegs.length === 0) return [];
        const paxLegBagLists = allLegs.map((leg) => compatibleBagsByLegForAllPax(bagType, leg.id ?? 0));
        if (allLegs.length === 1) return paxLegBagLists[0] ?? [];
        return allLegsBagsAreSymmetric(paxLegBagLists)
          ? (paxLegBagLists[0] ?? []).map((bag) => withAllLegsCombinedPrice(bag, paxLegBagLists))
          : [];
      };
    },
    compatibleBagsMergedLegsForPax(): (bagType: BagType, pax: Passenger) => BookableBag[] {
      const group = this.bagTypeGroup;
      const allLegs = this.allLegs;
      return (bagType: BagType, pax: Passenger) => {
        const entry = group[bagType];
        const paxBag = getPaxBag(entry, pax);
        if (allLegs.length === 0) return [];
        const allLegBagLists = allLegs.map((leg) => paxBag?.byLeg?.[leg.id ?? 0]?.bookableBags ?? []);
        if (allLegs.length === 1) return allLegBagLists[0] ?? [];
        return allLegsBagsAreSymmetric(allLegBagLists)
          ? (allLegBagLists[0] ?? []).map((bag) => withAllLegsCombinedPrice(bag, allLegBagLists))
          : [];
      };
    },
    // Included bags --
    compatibleIncludedBagsMergedLegsForAll(): (bagType: BagType) => BagAllowance[] {
      const group = this.bagTypeGroup;
      const allLegs = this.allLegs;
      const compatibleIncludedBagsByLegForAllPax = this.compatibleIncludedBagsByLegForAllPax;
      return (bagType: BagType) => {
        const entry = group[bagType];
        if (allLegs.length === 0) return [];
        const allPaxLegs = getEligibleLegsForAll(entry);
        if (allPaxLegs.length > 0) {
          const allPaxLegBagLists = allLegs.map((leg) => entry.allPassenger?.byLeg?.[leg.id ?? 0]?.includedBags ?? []);
          if (allLegs.length === 1) return allPaxLegBagLists[0] ?? [];
          return allLegsBagsAreSymmetric(allPaxLegBagLists) ? (allPaxLegBagLists[0] ?? []) : [];
        }
        // Eligibility here must come from included bags, not eligibleLegsForCompatiblePaxList (which
        // filters by bookable-bag compatibility) — a bag type with no purchasable options (e.g. HAND)
        // would otherwise always read as "diverges" regardless of its included allowance.
        const paxLegs = getEligibleLegsFromPaxList(entry).filter(
          (leg) => compatibleIncludedBagsByLegForAllPax(bagType, leg.id ?? 0).length > 0,
        );
        if (paxLegs.length === 0) return [];
        const paxLegBagLists = allLegs.map((leg) => compatibleIncludedBagsByLegForAllPax(bagType, leg.id ?? 0));
        if (allLegs.length === 1) return paxLegBagLists[0] ?? [];
        return allLegsBagsAreSymmetric(paxLegBagLists) ? (paxLegBagLists[0] ?? []) : [];
      };
    },
    compatibleIncludedBagsMergedLegsForPax(): (bagType: BagType, pax: Passenger) => BagAllowance[] {
      const group = this.bagTypeGroup;
      const allLegs = this.allLegs;
      return (bagType: BagType, pax: Passenger) => {
        const entry = group[bagType];
        const paxBag = getPaxBag(entry, pax);
        if (allLegs.length === 0) return [];
        const allLegBagLists = allLegs.map((leg) => paxBag?.byLeg?.[leg.id ?? 0]?.includedBags ?? []);
        if (allLegs.length === 1) return allLegBagLists[0] ?? [];
        return allLegsBagsAreSymmetric(allLegBagLists) ? (allLegBagLists[0] ?? []) : [];
      };
    },
    eligibleLegsForPax(): (bagType: BagType, pax: Passenger) => EligibleLeg[] {
      const group = this.bagTypeGroup;
      return (bagType: BagType, pax: Passenger) => getEligibleLegsForPax(group[bagType], pax);
    },
    eligibleLegsForCompatiblePaxList(): (bagType: BagType) => EligibleLeg[] {
      const group = this.bagTypeGroup;
      const compatibleBagsByLegForAllPax = this.compatibleBagsByLegForAllPax;
      return (bagType: BagType) => {
        const entry = group[bagType];
        return getEligibleLegsFromPaxList(entry).filter(
          (leg) => compatibleBagsByLegForAllPax(bagType, leg.id ?? 0).length > 0,
        );
      };
    },
    bookableBagsForAll(): (bagType: BagType) => BookableBag[] {
      const group = this.bagTypeGroup;
      return (bagType: BagType) => group[bagType].allPassenger?.byFlight?.bookableBags ?? [];
    },
    bookableBagsByLegForAll(): (bagType: BagType, legIndex: number) => BookableBag[] {
      const group = this.bagTypeGroup;
      return (bagType: BagType, legIndex: number) => group[bagType].allPassenger?.byLeg?.[legIndex]?.bookableBags ?? [];
    },
    selectedBagOnAnyLegForPax(): (bagType: BagType, pax: Passenger) => BookableBag | undefined {
      const allLegs = this.allLegs;
      const selectedByLegForPax = this.selectedByLegForPax;
      return (bagType: BagType, pax: Passenger) =>
        allLegs.map((leg) => selectedByLegForPax(bagType, leg.id ?? 0, pax)).find((bag) => bag !== undefined);
    },
    selectedByLegForPax(): (type: BagType, legIndex: number, pax: Passenger) => BookableBag | undefined {
      const group = this.bagTypeGroup;
      return (type: BagType, legIndex: number, pax: Passenger) =>
        selectedByLeg(getPaxBag(group[type], pax)?.byLeg?.[legIndex]?.bookableBags ?? [], legIndex, pax.selectedBags);
    },
    bookableBagsByLegForPax(): (bagType: BagType, legIndex: number, pax: Passenger) => BookableBag[] {
      const group = this.bagTypeGroup;
      return (bagType: BagType, legIndex: number, pax: Passenger) =>
        getPaxBag(group[bagType], pax)?.byLeg?.[legIndex]?.bookableBags ?? [];
    },
    bookableBagsForPax(): (bagType: BagType, pax: Passenger) => BookableBag[] {
      const group = this.bagTypeGroup;
      return (bagType: BagType, pax: Passenger) => getPaxBag(group[bagType], pax)?.byFlight?.bookableBags ?? [];
    },
    // Included bags --
    includedBagsForAll(): (bagType: BagType) => BagAllowance[] {
      const group = this.bagTypeGroup;
      return (bagType: BagType) => group[bagType].allPassenger?.byFlight?.includedBags ?? [];
    },
    includedBagsByLegForAll(): (bagType: BagType, legIndex: number) => BagAllowance[] {
      const group = this.bagTypeGroup;
      return (bagType: BagType, legIndex: number) => group[bagType].allPassenger?.byLeg?.[legIndex]?.includedBags ?? [];
    },
    includedBagsByLegForPax(): (bagType: BagType, legIndex: number, pax: Passenger) => BagAllowance[] {
      const group = this.bagTypeGroup;
      return (bagType: BagType, legIndex: number, pax: Passenger) =>
        getPaxBag(group[bagType], pax)?.byLeg?.[legIndex]?.includedBags ?? [];
    },
    includedBagsForPax(): (bagType: BagType, pax: Passenger) => BagAllowance[] {
      const group = this.bagTypeGroup;
      return (bagType: BagType, pax: Passenger) => getPaxBag(group[bagType], pax)?.byFlight?.includedBags ?? [];
    },
    // Piece count only: any other spec difference still counts as included.
    includedBagsMissingOnSomeLegs(): (bagType: BagType) => boolean {
      const perLeg = this.includedBagCountPerLegForAll;
      return (bagType: BagType) => {
        const counts = perLeg(bagType);
        return counts.some((count) => count > 0) && counts.some((count) => !count);
      };
    },
  },
  actions: {
    updateByFlightForAll(type: BagType, newBag?: BookableBag) {
      const entry = this.bagTypeGroup[type];
      const current = selectedByFlight(entry.allPassenger?.byFlight?.bookableBags ?? [], this.selectedBagsForAll);
      if (current) unSelectByFlight(current, this.selectedBagsForAll);
      if (newBag) selectByFlight(newBag, this.selectedBagsForAll);
    },
    selectedByFlightForAll(type: BagType): BookableBag | undefined {
      const entry = this.bagTypeGroup[type];
      return selectedByFlight(entry.allPassenger?.byFlight?.bookableBags ?? [], this.selectedBagsForAll);
    },
    updateByLegForAll(type: BagType, legIndex: number, newBag?: BookableBag) {
      const entry = this.bagTypeGroup[type];
      const current = selectedByLeg(
        entry.allPassenger?.byLeg?.[legIndex]?.bookableBags ?? [],
        legIndex,
        this.selectedBagsForAll,
      );
      if (current) unSelectByLeg(current, legIndex, this.selectedBagsForAll);
      if (newBag) selectByLeg(newBag, legIndex, this.selectedBagsForAll);
    },
    applyCompatibleBagToAllLegsForAll(type: BagType, reference?: BookableBag) {
      const entry = this.bagTypeGroup[type];
      const allPaxLegs = getEligibleLegsForAll(entry);
      if (allPaxLegs.length > 0) {
        for (const leg of allPaxLegs) {
          const legIndex = leg.id ?? 0;
          const bag = reference
            ? findCompatibleBag(reference, entry.allPassenger?.byLeg?.[legIndex]?.bookableBags ?? [])
            : undefined;
          this.updateByLegForAll(type, legIndex, bag);
        }
      } else {
        for (const leg of this.eligibleLegsForCompatiblePaxList(type)) {
          this.applyCompatibleBagByLegForToAllPax(type, leg.id ?? 0, reference);
        }
      }
    },
    selectedByLegForAll(type: BagType, legIndex: number): BookableBag | undefined {
      const entry = this.bagTypeGroup[type];
      return selectedByLeg(
        entry.allPassenger?.byLeg?.[legIndex]?.bookableBags ?? [],
        legIndex,
        this.selectedBagsForAll,
      );
    },
    initCompatibleBagToAllLegsForAll(type: BagType) {
      let firstSelected: BookableBag | undefined;
      // first choice: an existing all-pax per-leg selection
      for (const leg of getEligibleLegsForAll(this.bagTypeGroup[type])) {
        firstSelected = this.selectedByLegForAll(type, leg.id ?? 0);
        if (firstSelected) break;
      }
      // fallback: the first adult's own per-leg selection
      if (!firstSelected) {
        const entry = this.bagTypeGroup[type];
        const firstAdult = getFirstAdultPax(entry);
        if (firstAdult) {
          for (const leg of getEligibleLegsForPax(entry, firstAdult)) {
            firstSelected = this.selectedByLegForPax(type, leg.id ?? 0, firstAdult);
            if (firstSelected) break;
          }
        }
      }
      this.applyCompatibleBagToAllLegsForAll(type, firstSelected);
    },
    initCompatibleBagByLegForToAllPax(type: BagType) {
      let firstSelected: BookableBag | undefined;
      for (const leg of this.eligibleLegsForCompatiblePaxList(type)) {
        firstSelected = this.selectedCompatibleByLegAllPax(type, leg.id ?? 0);
        if (firstSelected) break;
      }
      for (const leg of this.eligibleLegsForCompatiblePaxList(type)) {
        this.applyCompatibleBagByLegForToAllPax(type, leg.id ?? 0, firstSelected);
      }
    },
    updateByFlightForPax(type: BagType, pax: Passenger, newBag?: BookableBag) {
      this.reportBagGesture(type, () => {
        setBagByFlightForPax(this.bagTypeGroup[type], pax, newBag);
      });
    },
    selectedByFlightForPax(type: BagType, pax: Passenger): BookableBag | undefined {
      return selectedByFlight(getPaxBag(this.bagTypeGroup[type], pax)?.byFlight?.bookableBags ?? [], pax.selectedBags);
    },
    updateByLegForPax(type: BagType, legIndex: number, pax: Passenger, newBag?: BookableBag) {
      this.reportBagGesture(type, () => {
        setBagByLegForPax(this.bagTypeGroup[type], legIndex, pax, newBag);
      });
    },
    applyCompatibleBagToAllLegsForPax(type: BagType, pax: Passenger, reference?: BookableBag) {
      this.reportBagGesture(type, () => {
        applyBagToEveryLegForPax(this.bagTypeGroup[type], pax, reference);
      });
    },
    initCompatibleBagToAllLegsForPax(type: BagType, pax: Passenger) {
      let firstSelected: BookableBag | undefined;
      // first choice: this pax's own per-leg selection
      for (const leg of getEligibleLegsForPax(this.bagTypeGroup[type], pax)) {
        firstSelected = this.selectedByLegForPax(type, leg.id ?? 0, pax);
        if (firstSelected) break;
      }
      // fallback: the shared all-pax selection
      if (!firstSelected) {
        for (const leg of getEligibleLegsForAll(this.bagTypeGroup[type])) {
          firstSelected = this.selectedByLegForAll(type, leg.id ?? 0);
          if (firstSelected) break;
        }
      }
      this.reportBagGesture(type, () => {
        applyBagToEveryLegForPax(this.bagTypeGroup[type], pax, firstSelected);
      });
    },

    // GA4 names what the traveller just did, so a gesture measures its own effect. Nothing remembers
    // a previous cart: the nesting paths write through the silent helpers at the bottom of the file.
    reportBagGesture(type: BagType, gesture: () => void) {
      const count = this.selectedCount(type);
      const value = this.selectedBagsPriceByType(type);
      gesture();
      useGtmStore().trackBagChange(type, count, value, this.selectedCount(type), this.selectedBagsPriceByType(type));
    },
    selectedCompatibleAllPaxMergedLegs(type: BagType): BookableBag | undefined {
      const entry = this.bagTypeGroup[type];
      const mergedOptions = this.compatibleBagsMergedLegsForAll(type);
      const allPaxLegs = getEligibleLegsForAll(entry);
      if (allPaxLegs.length > 0) {
        const selected = this.selectedByLegForAll(type, allPaxLegs[0]?.id ?? 0);
        return selected ? findCompatibleBag(selected, mergedOptions) : undefined;
      }
      const paxLegs = this.eligibleLegsForCompatiblePaxList(type);
      if (paxLegs.length === 0) return undefined;
      const selected = this.selectedCompatibleByLegAllPax(type, paxLegs[0]?.id ?? 0);
      return selected ? findCompatibleBag(selected, mergedOptions) : undefined;
    },
    selectedCompatibleByPaxMergedLeg(type: BagType, pax: Passenger): BookableBag | undefined {
      const firstLeg = getEligibleLegsForPax(this.bagTypeGroup[type], pax)[0];
      const selected = firstLeg ? this.selectedByLegForPax(type, firstLeg.id ?? 0, pax) : undefined;
      return selected ? findCompatibleBag(selected, this.compatibleBagsMergedLegsForPax(type, pax)) : undefined;
    },
    selectedCompatibleByFlightAllPax(type: BagType): BookableBag | undefined {
      const entry = this.bagTypeGroup[type];
      const firstAdult = getFirstAdultPax(entry);
      const selected = firstAdult ? this.selectedByFlightForPax(type, firstAdult) : undefined;
      return selected ? withAllPaxCombinedPrice(selected, entry) : undefined;
    },
    selectedCompatibleByLegAllPax(type: BagType, legIndex: number): BookableBag | undefined {
      const entry = this.bagTypeGroup[type];
      const firstAdult = getFirstAdultPax(entry);
      const selected = firstAdult ? this.selectedByLegForPax(type, legIndex, firstAdult) : undefined;
      return selected ? withAllPaxCombinedPrice(selected, entry, legIndex) : undefined;
    },
    applyCompatibleBagByFlightToAllPax(type: BagType, reference?: BookableBag) {
      for (const paxGroup of Object.values(this.bagTypeGroup[type].passengerByType ?? {})) {
        for (const pb of paxGroup) {
          const compatible = reference ? findCompatibleBag(reference, pb.byFlight?.bookableBags ?? []) : undefined;
          setBagByFlightForPax(this.bagTypeGroup[type], pb.pax, compatible);
        }
      }
    },
    applyCompatibleBagByLegForToAllPax(type: BagType, legIndex: number, reference?: BookableBag) {
      for (const paxGroup of Object.values(this.bagTypeGroup[type].passengerByType ?? {})) {
        for (const pb of paxGroup) {
          const compatible = reference
            ? findCompatibleBag(reference, pb.byLeg?.[legIndex]?.bookableBags ?? [])
            : undefined;
          setBagByLegForPax(this.bagTypeGroup[type], legIndex, pb.pax, compatible);
        }
      }
    },
    reset() {
      this.$reset();
      for (const type of this.bagTypeList) {
        for (const pax of getAllPax(this.bagTypeGroup[type])) {
          pax.selectedBags = undefined;
        }
      }
    },
    repriceSelectedBags() {
      const bookableBags = this.bagTypeList.flatMap((bagType) => this.bagTypeGroup[bagType].bookableBags);
      repriceSelection(bookableBags, this.selectedBagsForAll);
      for (const pax of usePassengersStore().passengers) {
        repriceSelection(bookableBags, pax.selectedBags);
      }
    },
  },
});

// used by repriceSelectedBags
function repriceSelection(bookableBags: BookableBag[], selectedBags?: SelectedBags) {
  for (const selection of [selectedBags?.byFlight, ...Object.values(selectedBags?.byLeg ?? {})]) {
    for (const [key, selectedBag] of Object.entries(selection ?? {})) {
      const bookableBag = findSelectedBag(bookableBags, key, selectedBag);
      if (selection && bookableBag) {
        selection[key] = toSelectedBag(bookableBag);
      }
    }
  }
}

function toSelectedBag(bookableBag: BookableBag): SelectedBag {
  return {
    type: bookableBag.type,
    source: bookableBag.source,
    providerId: bookableBag.providerId,
    uid: bookableBag.uid,
    price: bookableBag.price,
    pieces: bookableBag.pieces,
  };
}

function mergedIfUniform(counts: number[]): number | undefined {
  return counts.every((count) => count === counts[0]) ? counts[0] : undefined;
}

// used by updateByFlightForAll, selectByFlightForPax
function selectByFlight(bookableBag: BookableBag, selectedBags: SelectedBags) {
  const selectedBag = toSelectedBag(bookableBag);
  const key = computeKey(bookableBag);
  selectedBags.byFlight ??= {};
  selectedBags.byFlight[key] = selectedBag;
}

// used by selectedByFlightForAll, selectedByFlightForPax, updateByFlightForAll, updateByFlightForPax
function selectedByFlight(bookableBags: BookableBag[], selectedBags?: SelectedBags): BookableBag | undefined {
  return bookableBags.find((bag) => {
    if (!selectedBags?.byFlight) {
      return;
    }
    for (const byFlight of Object.values(selectedBags.byFlight)) {
      if (computeKey(bag) === computeKey(byFlight)) {
        return bag;
      }
    }
  });
}

// used by updateByFlightForAll, unSelectByFlightForPax
function unSelectByFlight(bookableBag: BookableBag, selectedBags: SelectedBags) {
  const key = computeKey(bookableBag);
  if (selectedBags?.byFlight?.[key]) {
    selectedBags.byFlight = Object.fromEntries(Object.entries(selectedBags.byFlight).filter(([k]) => k !== key));
  }
}

// used by updateByFlightForPax
function selectByFlightForPax(bookableBag: BookableBag, pax: Passenger) {
  pax.selectedBags ??= new modelsImpl.SelectedBagsImpl();
  selectByFlight(bookableBag, pax.selectedBags);
}

// used by updateByFlightForPax
function unSelectByFlightForPax(bookableBag: BookableBag, pax: Passenger) {
  if (pax.selectedBags) {
    unSelectByFlight(bookableBag, pax.selectedBags);
  }
}

// used by updateByLegForAll, selectByLegForPax
function selectByLeg(bookableBag: BookableBag, legIndex: number, selectedBags: SelectedBags) {
  const selectedBag = toSelectedBag(bookableBag);
  const key = computeKey(bookableBag);
  selectedBags.byLeg ??= {};
  selectedBags.byLeg[legIndex] ??= {};
  selectedBags.byLeg[legIndex][key] = selectedBag;
}

// used by selectedByLegForAll, selectedByLegForPax, updateByLegForAll, updateByLegForPax
function selectedByLeg(
  bookableBags: BookableBag[],
  legIndex: number,
  selectedBags?: SelectedBags,
): BookableBag | undefined {
  return bookableBags.find((bag) => {
    if (!selectedBags?.byLeg?.[legIndex]) {
      return;
    }
    for (const byLeg of Object.values(selectedBags.byLeg[legIndex])) {
      if (computeKey(bag) === computeKey(byLeg)) {
        return bag;
      }
    }
  });
}

// used by updateByLegForAll, unSelectByLegForPax
function unSelectByLeg(bookableBag: BookableBag, legIndex: number, selectedBags: SelectedBags) {
  const key = computeKey(bookableBag);
  if (selectedBags?.byLeg?.[legIndex]?.[key]) {
    selectedBags.byLeg[legIndex] = Object.fromEntries(
      Object.entries(selectedBags.byLeg[legIndex]).filter(([k]) => k !== key),
    );
  }
}

// used by updateByLegForPax
function selectByLegForPax(bookableBag: BookableBag, legIndex: number, pax: Passenger) {
  pax.selectedBags ??= new modelsImpl.SelectedBagsImpl();
  selectByLeg(bookableBag, legIndex, pax.selectedBags);
}

// used by updateByLegForPax
function unSelectByLegForPax(bookableBag: BookableBag, legIndex: number, pax: Passenger) {
  if (pax.selectedBags) {
    unSelectByLeg(bookableBag, legIndex, pax.selectedBags);
  }
}

// Guard: ancillary CABIN bags have hardcoded uid 'SINGLE_OPTION', so all options share key 'CABINANCILLARYSINGLE_OPTION'.
// Selecting a new ancillary option overwrites the previous one — single-selection enforced by key structure.
function computeKey(bookableBag: BookableBag | SelectedBag) {
  return bookableBag.type + bookableBag.source + (bookableBag.uid ?? '');
}

function selectedPiecesForPax(bagType: BagType, selectedBags?: SelectedBags): number {
  for (const bag of Object.values(selectedBags?.byFlight ?? {})) {
    if (bag.type === bagType) {
      return bag.pieces ?? 1;
    }
  }
  // Same bag is repeated on each leg — max across legs gives the physical piece count, not sum.
  let physicalLegPieces = 0;
  for (const legBags of Object.values(selectedBags?.byLeg ?? {})) {
    for (const bag of Object.values(legBags)) {
      if (bag.type === bagType) {
        physicalLegPieces = Math.max(physicalLegPieces, bag.pieces ?? 1);
      }
    }
  }
  return physicalLegPieces;
}

// used by repriceSelection, sumSelectedBagsPrice
function findSelectedBag(bookableBags: BookableBag[], key: string, selectedBag: SelectedBag): BookableBag | undefined {
  return bookableBags.find((bag) => computeKey(bag) === key && bag.providerId === selectedBag.providerId);
}

// used by selectedBagsPriceByTypeIn
function sumSelectedBagsPrice(bookableBags: BookableBag[], type: BagType, selectedBags?: SelectedBags) {
  let total = 0;
  for (const selection of [selectedBags?.byFlight, ...Object.values(selectedBags?.byLeg ?? {})]) {
    for (const [key, bag] of Object.entries(selection ?? {})) {
      if (bag.type === type) {
        total += findSelectedBag(bookableBags, key, bag)?.price ?? 0;
      }
    }
  }
  return total;
}

function populatePassengerBags(
  bookableBag: BookableBag,
  allPassenger: AllPassengerBag,
  passengerByType?: PassengerBagByPassengerType,
) {
  dispatchBagsForAll(bookableBag, allPassenger);
  if (!passengerByType) return;
  for (const paxType of Object.values(PaxTypeEnum)) {
    if (passengerByType[paxType] && bookableBag.eligiblePaxTypes?.includes(paxType)) {
      for (const passengerBag of passengerByType[paxType]) {
        dispatchBagsByPax(bookableBag, passengerBag);
      }
    }
  }
}

function populateIncludedBag(
  includedBag: BagAllowance,
  allPassenger: AllPassengerBag,
  passengerByType?: PassengerBagByPassengerType,
) {
  dispatchIncludedBagForAll(includedBag, allPassenger);
  if (!passengerByType) return;
  for (const paxType of Object.values(PaxTypeEnum)) {
    if (passengerByType[paxType] && includedBag.eligiblePaxTypes?.includes(paxType)) {
      for (const passengerBag of passengerByType[paxType]) {
        dispatchIncludedBagByPax(includedBag, passengerBag);
      }
    }
  }
}

function dispatchBagsForAll(bag: BookableBag, pax: AllPassengerBag) {
  if (bag.bookableBy === 'ALL_PAX_FLIGHT') {
    dispatchBagsByFlight(bag, pax);
  } else if (bag.bookableBy === 'ALL_PAX_LEG') {
    dispatchBagsByLeg(bag, pax);
  }
}

function dispatchBagsByPax(bag: BookableBag, pax: AllPassengerBag) {
  if (bag.bookableBy === 'FLIGHT') {
    dispatchBagsByFlight(bag, pax);
  } else if (bag.bookableBy === 'LEG') {
    dispatchBagsByLeg(bag, pax);
  }
}

function dispatchBagsByFlight(bag: BookableBag, pax: AllPassengerBag) {
  pax.byFlight ??= { bookableBags: [], includedBags: [] };
  pax.byFlight.bookableBags ??= [];
  pax.byFlight.bookableBags.push(bag);
}

function dispatchBagsByLeg(bag: BookableBag, pax: AllPassengerBag) {
  for (const leg of bag.eligibleLegs ?? []) {
    if (leg.id !== undefined) {
      const slot = getOrCreatePassengerBagByLeg(pax, leg, leg.id);
      slot.bookableBags ??= [];
      slot.bookableBags.push(bag);
    }
  }
}

function dispatchIncludedBagForAll(includedBag: BagAllowance, pax: AllPassengerBag) {
  if (includedBag.bookableBy === 'ALL_PAX_FLIGHT') {
    dispatchIncludedBagByFlight(includedBag, pax);
  } else if (includedBag.bookableBy === 'ALL_PAX_LEG') {
    dispatchIncludedBagByLeg(includedBag, pax);
  }
}

function dispatchIncludedBagByPax(includedBag: BagAllowance, pax: AllPassengerBag) {
  if (includedBag.bookableBy === 'FLIGHT') {
    dispatchIncludedBagByFlight(includedBag, pax);
  } else if (includedBag.bookableBy === 'LEG') {
    dispatchIncludedBagByLeg(includedBag, pax);
  }
}

function dispatchIncludedBagByFlight(includedBag: BagAllowance, pax: AllPassengerBag) {
  pax.byFlight ??= { bookableBags: [], includedBags: [] };
  pax.byFlight.includedBags ??= [];
  pax.byFlight.includedBags.push(includedBag);
}

function dispatchIncludedBagByLeg(includedBag: BagAllowance, pax: AllPassengerBag) {
  for (const leg of includedBag.eligibleLegs ?? []) {
    if (leg.id !== undefined) {
      getOrCreatePassengerBagByLeg(pax, leg, leg.id).includedBags.push(includedBag);
    }
  }
}

function getOrCreatePassengerBagByLeg(pax: AllPassengerBag, leg: EligibleLeg, legIndex: number): PassengerBagLeg {
  pax.byLeg ??= {};
  const passengerBagByLeg = pax.byLeg[legIndex];
  if (passengerBagByLeg) {
    return passengerBagByLeg;
  }

  const initPassengerBagByLeg = {
    eligibleLeg: leg,
    bookableBags: [],
    includedBags: [],
  };
  pax.byLeg[legIndex] = initPassengerBagByLeg;
  return initPassengerBagByLeg;
}

function buildBagTypeGroupEntry(
  type: BagType,
  groupedPassengers: Partial<Record<keyof typeof PaxTypeEnum, Passenger[]>>,
  allBookableBag: BookableBag[],
  allIncludedBags: BagAllowance[],
): BagTypeAvailability {
  const bookableBags = allBookableBag.filter((b) => b.type === type);
  const includedBags = allIncludedBags.filter((b) => b.type === type);
  const passengerByType: PassengerBagByPassengerType = {
    [PaxTypeEnum.ADULT]: groupedPassengers.ADULT?.map((pax) => ({ pax })) ?? [],
    [PaxTypeEnum.CHILD]: groupedPassengers.CHILD?.map((pax) => ({ pax })) ?? [],
    [PaxTypeEnum.INFANT]: groupedPassengers.INFANT?.map((pax) => ({ pax })) ?? [],
  };
  const { pax, ...allPassenger } = new modelsImpl.PassengerBagImpl();
  for (const bag of bookableBags) populatePassengerBags(bag, allPassenger, passengerByType);
  for (const bag of includedBags) populateIncludedBag(bag, allPassenger, passengerByType);
  return { bookableBags, includedBags, allPassenger, passengerByType };
}

function bagForPax(
  includedBags: BagAllowance[],
  paxType: PaxType,
  presentPaxTypes: PaxType[],
): BagAllowance | undefined {
  return includedBags.find((bag) =>
    (bag.eligiblePaxTypes?.length ? bag.eligiblePaxTypes : presentPaxTypes).includes(paxType),
  );
}

function isEligibleOnLeg(bag: BagAllowance, legId: number): boolean {
  return !bag.eligibleLegs?.length || bag.eligibleLegs.some((leg) => leg.id === legId);
}

function includedFlightPieces(source?: AllPassengerBag): number {
  return (source?.byFlight?.includedBags ?? []).reduce((sum, bag) => sum + (bag.pieces ?? 0), 0);
}

function includedLegPieces(legId: number, source?: AllPassengerBag): number {
  return (source?.byLeg?.[legId]?.includedBags ?? []).reduce((sum, bag) => sum + (bag.pieces ?? 0), 0);
}

// byFlight pieces apply to every leg; byLeg pieces are added on top of them for their own leg.
function includedCountsPerLeg(sources: (AllPassengerBag | undefined)[], legs: Leg[]): number[] {
  const flightPieces = sources.reduce((sum, source) => sum + includedFlightPieces(source), 0);
  return legs.map(
    (_leg, legId) => flightPieces + sources.reduce((sum, source) => sum + includedLegPieces(legId, source), 0),
  );
}

function totalCountsPerLeg(
  includedSources: (AllPassengerBag | undefined)[],
  selectedSources: (SelectedBags | undefined)[],
  bagType: BagType,
  legs: Leg[],
): number[] {
  const flightPieces = selectedSources.reduce((sum, selected) => sum + selectedFlightPieces(bagType, selected), 0);
  // byFlight pieces apply to every leg; byLeg pieces are added on top of them for their own leg.
  return includedCountsPerLeg(includedSources, legs).map(
    (included, legId) =>
      included +
      flightPieces +
      selectedSources.reduce((sum, selected) => sum + selectedLegPieces(bagType, legId, selected), 0),
  );
}

function selectedFlightPieces(bagType: BagType, selectedBags?: SelectedBags): number {
  return Object.values(selectedBags?.byFlight ?? {})
    .filter((bag) => bag.type === bagType)
    .reduce((sum, bag) => sum + (bag.pieces ?? 1), 0);
}

function selectedLegPieces(bagType: BagType, legId: number, selectedBags?: SelectedBags): number {
  return Object.values(selectedBags?.byLeg?.[legId] ?? {})
    .filter((bag) => bag.type === bagType)
    .reduce((sum, bag) => sum + (bag.pieces ?? 1), 0);
}

function getEligibleLegsForAll(entry: BagTypeAvailability): EligibleLeg[] {
  const byLeg = entry.allPassenger?.byLeg;
  if (!byLeg) return [];
  return Object.values(byLeg).map((d) => d.eligibleLeg);
}

function setBagByFlightForPax(entry: BagTypeAvailability, pax: Passenger, newBag?: BookableBag) {
  const paxBag = getPaxBag(entry, pax);
  const current = selectedByFlight(paxBag?.byFlight?.bookableBags ?? [], pax.selectedBags);
  if (current) {
    unSelectByFlightForPax(current, pax);
  }
  if (newBag) {
    selectByFlightForPax(newBag, pax);
  }
}

function setBagByLegForPax(entry: BagTypeAvailability, legIndex: number, pax: Passenger, newBag?: BookableBag) {
  const paxBag = getPaxBag(entry, pax);
  const current = selectedByLeg(paxBag?.byLeg?.[legIndex]?.bookableBags ?? [], legIndex, pax.selectedBags);
  if (current) {
    unSelectByLegForPax(current, legIndex, pax);
  }
  if (newBag) {
    selectByLegForPax(newBag, legIndex, pax);
  }
}

function applyBagToEveryLegForPax(entry: BagTypeAvailability, pax: Passenger, reference?: BookableBag) {
  const paxBag = getPaxBag(entry, pax);
  for (const leg of getEligibleLegsForPax(entry, pax)) {
    const legIndex = leg.id ?? 0;
    const bag = reference ? findCompatibleBag(reference, paxBag?.byLeg?.[legIndex]?.bookableBags ?? []) : undefined;
    setBagByLegForPax(entry, legIndex, pax, bag);
  }
}

function getPaxBag(entry: BagTypeAvailability, pax: Passenger): PassengerBag | undefined {
  return entry.passengerByType?.[pax.type]?.find((pb) => pb.pax.id === pax.id);
}

function getEligibleLegsForPax(entry: BagTypeAvailability, pax: Passenger): EligibleLeg[] {
  const byLeg = getPaxBag(entry, pax)?.byLeg;
  if (!byLeg) return [];
  return Object.values(byLeg).map((d) => d.eligibleLeg);
}

function getEligibleLegsFromPaxList(entry: BagTypeAvailability): EligibleLeg[] {
  const seen = new Set<number>();
  const legs: EligibleLeg[] = [];
  for (const paxGroup of Object.values(entry.passengerByType ?? {})) {
    for (const pb of paxGroup) {
      for (const leg of getEligibleLegsForPax(entry, pb.pax)) {
        if (!seen.has(leg.id ?? 0)) {
          seen.add(leg.id ?? 0);
          legs.push(leg);
        }
      }
    }
  }
  return legs;
}

function findCompatibleBag<T extends BookableBag | BagAllowance>(
  reference: BookableBag | BagAllowance,
  candidates: T[],
): T | undefined {
  return candidates.find((b) => bagsHaveSameSpecAndPieces(reference, b));
}

function bagsHaveSameSpec(a: BookableBag | BagAllowance, b: BookableBag | BagAllowance): boolean {
  return (
    a.weight === b.weight &&
    a.maxWeight === b.maxWeight &&
    a.combinedWeight === b.combinedWeight &&
    a.height === b.height &&
    a.width === b.width &&
    a.depth === b.depth &&
    a.dropAtCheckIn === b.dropAtCheckIn &&
    a.description === b.description
  );
}

function bagsHaveSameSpecAndPieces(a: BookableBag | BagAllowance, b: BookableBag | BagAllowance): boolean {
  return a.pieces === b.pieces && bagsHaveSameSpec(a, b);
}

// Bags bookable/included for every pax type at once: take the first group as reference and keep
// only the bags that have a compatible match in every other group. Channel-agnostic — the selector
// picks byFlight/byLeg and bookableBags/includedBags; the caller layers price-mapping on top.
function compatibleAcrossPaxGroups<T extends BookableBag | BagAllowance>(
  entry: BagTypeAvailability,
  select: (paxBag?: PassengerBag) => T[],
): T[] {
  const paxTypeGroups = Object.values(entry.passengerByType ?? {}).filter((paxGroup) => paxGroup.length > 0);
  if (paxTypeGroups.length === 0) {
    return [];
  }

  const [referenceGroup, ...otherGroups] = paxTypeGroups;
  const referenceBags = select(referenceGroup?.[0]);
  const otherBagLists = otherGroups.map((paxGroup) => select(paxGroup[0]));
  return referenceBags.filter((bag) => otherBagLists.every((list) => findCompatibleBag(bag, list) !== undefined));
}

function allLegsBagsAreSymmetric<T extends BookableBag | BagAllowance>(allLegBagLists: T[][]): boolean {
  const firstLength = allLegBagLists[0]?.length ?? 0;
  if (!allLegBagLists.every((legBags) => legBags.length === firstLength)) {
    return false;
  }

  return allLegBagLists.every((legBags, legIndex) => {
    const otherLists = allLegBagLists.filter((_, otherIndex) => otherIndex !== legIndex);
    return legBags.every((bag) => otherLists.every((list) => findCompatibleBag(bag, list) !== undefined));
  });
}

function getAllLegIndices(entry: BagTypeAvailability): number[] {
  const max = Math.max(-1, ...entry.bookableBags.flatMap((b) => b.eligibleLegs?.map((l) => l.id ?? 0) ?? []));
  return max >= 0 ? Array.from({ length: max + 1 }, (_, index) => index) : [];
}

function countTotalPax(entry: BagTypeAvailability): number {
  return Object.values(entry.passengerByType ?? {}).reduce((sum, g) => sum + (g?.length ?? 0), 0);
}

function getFirstAdultPax(entry: BagTypeAvailability): Passenger | undefined {
  return entry.passengerByType?.[PaxTypeEnum.ADULT]?.[0]?.pax;
}

function getAllPax(entry: BagTypeAvailability): Passenger[] {
  return Object.values(entry.passengerByType ?? {})
    .flat()
    .map((pb) => pb.pax);
}

function withAllLegsCombinedPrice(bag: BookableBag, allLegBagLists: BookableBag[][]): BookableBag {
  let total = 0;
  for (const legBags of allLegBagLists) {
    total += findCompatibleBag(bag, legBags)?.price ?? 0;
  }
  return { ...bag, price: total };
}

function withAllPaxCombinedPrice(bag: BookableBag, entry: BagTypeAvailability, legIndex?: number): BookableBag {
  let total = 0;
  for (const paxGroup of Object.values(entry.passengerByType ?? {})) {
    const firstPax = paxGroup[0];
    const candidates =
      legIndex === undefined
        ? (firstPax?.byFlight?.bookableBags ?? [])
        : (firstPax?.byLeg?.[legIndex]?.bookableBags ?? []);
    const compatible = findCompatibleBag(bag, candidates);
    if (compatible) total += (compatible.price ?? 0) * paxGroup.length;
  }
  return { ...bag, price: total };
}
